import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";
import {
  DEBRIEFS_FETCH,
  DEBRIEFS_FETCH_SUCCESS,
  DEBRIEFS_FETCH_FAILURE,
  DEBRIEF_FETCH,
  DEBRIEF_FETCH_SUCCESS,
  DEBRIEF_FETCH_FAILURE,
  OBJECTIVES_FETCH,
  OBJECTIVES_FETCH_SUCCESS,
  OBJECTIVES_FETCH_FAILURE,
} from "../actionTypes";
import { auth } from "../../conf/firebase";

const BASE_URL = () => `${getConfig().API_URL}/audio/api/v1/debriefs`;

// Fetch all debriefs with optional filters
export const fetchDebriefs = (filters = {}) => {
  return async (dispatch) => {
    const { currentUser } = auth;
    if (!currentUser) return;

    try {
      dispatch({ type: DEBRIEFS_FETCH });

      const params = new URLSearchParams();
      if (filters.operator_id) params.append("operator_id", filters.operator_id);
      if (filters.status) params.append("status", filters.status);
      if (filters.date_start) params.append("date_start", filters.date_start);
      if (filters.date_end) params.append("date_end", filters.date_end);
      // "exclude" (backend default) | "only" | "include"
      if (filters.archived) params.append("archived", filters.archived);

      const url = params.toString() ? `${BASE_URL()}?${params.toString()}` : BASE_URL();
      const response = await myAxios.get(url);

      dispatch({
        type: DEBRIEFS_FETCH_SUCCESS,
        payload: response?.data?.[0]?.debriefs || [],
      });
    } catch (error) {
      console.error("Error fetching debriefs:", error);
      dispatch({ type: DEBRIEFS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Fetch single debrief by ID
export const fetchDebrief = (debriefId) => {
  return async (dispatch) => {
    const { currentUser } = auth;
    if (!currentUser) return;

    try {
      dispatch({ type: DEBRIEF_FETCH });

      const response = await myAxios.get(`${BASE_URL()}/${debriefId}`);

      dispatch({
        type: DEBRIEF_FETCH_SUCCESS,
        payload: response?.data?.[0]?.debrief || null,
      });
    } catch (error) {
      console.error("Error fetching debrief:", error);
      dispatch({ type: DEBRIEF_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create a new debrief
export const createDebrief = async (operatorId, periodStart, periodEnd, scheduledDate = null) => {
  const { currentUser } = auth;
  if (!currentUser) return null;

  try {
    const body = {
      operator_id: operatorId,
      period_start: periodStart,
      period_end: periodEnd,
    };
    if (scheduledDate) {
      body.scheduled_date = scheduledDate;
    }
    const response = await myAxios.post(BASE_URL(), body);

    return response?.data?.[0]?.debrief_id || null;
  } catch (error) {
    console.error("Error creating debrief:", error);
    throw error;
  }
};

// Update a debrief
export const updateDebrief = async (debriefId, updates) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.put(`${BASE_URL()}/${debriefId}`, updates);
    return true;
  } catch (error) {
    console.error("Error updating debrief:", error);
    throw error;
  }
};

// Archive / unarchive a debrief (hidden from the default list, kept in history)
export const setDebriefArchived = async (debriefId, archived) =>
  updateDebrief(debriefId, { archived: !!archived });

// Delete a debrief
export const deleteDebrief = async (debriefId) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.delete(`${BASE_URL()}/${debriefId}`);
    return true;
  } catch (error) {
    console.error("Error deleting debrief:", error);
    throw error;
  }
};

// Get last debrief for an operator
export const fetchLastDebriefForOperator = async (operatorId) => {
  const { currentUser } = auth;
  if (!currentUser) return null;

  try {
    const response = await myAxios.get(`${BASE_URL()}/operator/${operatorId}/last`);
    return response?.data?.[0]?.debrief || null;
  } catch (error) {
    console.error("Error fetching last debrief:", error);
    return null;
  }
};

// Get KPIs preview for an operator
export const fetchOperatorKpis = async (operatorId, periodStart, periodEnd) => {
  const { currentUser } = auth;
  if (!currentUser) return null;

  try {
    const params = new URLSearchParams({
      period_start: periodStart,
      period_end: periodEnd,
    });
    const response = await myAxios.get(
      `${BASE_URL()}/operator/${operatorId}/kpis?${params.toString()}`
    );
    return response?.data?.[0]?.kpis || null;
  } catch (error) {
    console.error("Error fetching operator KPIs:", error);
    return null;
  }
};

// =====================
// COMMENTS
// =====================

// Add comment to a debrief
export const addDebriefComment = async (
  debriefId,
  content,
  callId = null,
  audioTimestamp = null
) => {
  const { currentUser } = auth;
  if (!currentUser) return null;

  try {
    const response = await myAxios.post(`${BASE_URL()}/${debriefId}/comments`, {
      content,
      call_id: callId,
      audio_timestamp: audioTimestamp,
    });
    return response?.data?.[0]?.comment_id || null;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// Update comment
export const updateDebriefComment = async (commentId, content) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.put(`${BASE_URL()}/comments/${commentId}`, { content });
    return true;
  } catch (error) {
    console.error("Error updating comment:", error);
    throw error;
  }
};

// Delete comment
export const deleteDebriefComment = async (commentId) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.delete(`${BASE_URL()}/comments/${commentId}`);
    return true;
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};

// =====================
// OBJECTIVES
// =====================

// Fetch objectives for an operator
export const fetchObjectives = (operatorId, includeInactive = false) => {
  return async (dispatch) => {
    const { currentUser } = auth;
    if (!currentUser) return;

    try {
      dispatch({ type: OBJECTIVES_FETCH });

      const params = includeInactive ? "?include_inactive=true" : "";
      const response = await myAxios.get(`${BASE_URL()}/objectives/${operatorId}${params}`);

      dispatch({
        type: OBJECTIVES_FETCH_SUCCESS,
        payload: response?.data?.[0]?.objectives || [],
      });
    } catch (error) {
      console.error("Error fetching objectives:", error);
      dispatch({ type: OBJECTIVES_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create a new objective
export const createObjective = async (operatorId, name, type, targetValue) => {
  const { currentUser } = auth;
  if (!currentUser) return null;

  try {
    const response = await myAxios.post(`${BASE_URL()}/objectives`, {
      operator_id: operatorId,
      name,
      type,
      target_value: targetValue,
    });
    return response?.data?.[0]?.objective_id || null;
  } catch (error) {
    console.error("Error creating objective:", error);
    throw error;
  }
};

// Update an objective
export const updateObjective = async (objectiveId, updates) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.put(`${BASE_URL()}/objectives/${objectiveId}`, updates);
    return true;
  } catch (error) {
    console.error("Error updating objective:", error);
    throw error;
  }
};

// Disable an objective
export const disableObjective = async (objectiveId) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.put(`${BASE_URL()}/objectives/${objectiveId}/disable`);
    return true;
  } catch (error) {
    console.error("Error disabling objective:", error);
    throw error;
  }
};

// =====================
// CALLS
// =====================

// Add calls to a debrief
export const addCallsToDebrief = async (debriefId, callIds) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.put(`${BASE_URL()}/${debriefId}/add_calls`, { call_ids: callIds });
    return true;
  } catch (error) {
    console.error("Error adding calls to debrief:", error);
    throw error;
  }
};

// Remove a call from a debrief
export const removeCallFromDebrief = async (debriefId, callId) => {
  const { currentUser } = auth;
  if (!currentUser) return false;

  try {
    await myAxios.put(`${BASE_URL()}/${debriefId}/remove_call/${callId}`);
    return true;
  } catch (error) {
    console.error("Error removing call from debrief:", error);
    throw error;
  }
};

// Fetch calls attached to a debrief
export const fetchDebriefCalls = async (debriefId) => {
  const { currentUser } = auth;
  if (!currentUser) return [];

  try {
    const response = await myAxios.get(`${BASE_URL()}/${debriefId}/calls`);
    return response?.data?.[0]?.calls || [];
  } catch (error) {
    console.error("Error fetching debrief calls:", error);
    return [];
  }
};

// Fetch objective grade history for an operator (last 5 debriefs)
export const fetchObjectiveHistory = async (operatorId) => {
  const { currentUser } = auth;
  if (!currentUser) return [];

  try {
    const response = await myAxios.get(`${BASE_URL()}/operator/${operatorId}/objective_history`);
    return response?.data?.[0]?.history || [];
  } catch (error) {
    console.error("Error fetching objective history:", error);
    return [];
  }
};
